import { type ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/cn';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  className?: string;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'full';
  hideClose?: boolean;
}

const sizes = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  full: 'max-w-none w-screen h-screen rounded-none',
};

export function Modal({
  open,
  onClose,
  title,
  children,
  className,
  footer,
  size = 'md',
  hideClose,
}: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 animate-fade-in">
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      <div
        className={cn(
          'relative w-full bg-bg-surface border border-white/5 shadow-card overflow-hidden flex flex-col',
          'rounded-t-2xl sm:rounded-2xl animate-slide-up',
          'max-h-[92dvh]',
          sizes[size],
          className,
        )}
      >
        {(title || !hideClose) && (
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/5 shrink-0">
            <h2 className="text-base font-display font-semibold text-white">{title}</h2>
            {!hideClose && (
              <button
                onClick={onClose}
                className="text-white/50 hover:text-white text-2xl leading-none w-9 h-9 inline-flex items-center justify-center rounded-lg hover:bg-white/5"
                aria-label="Close"
              >
                ×
              </button>
            )}
          </div>
        )}
        <div className="overflow-y-auto flex-1 min-h-0">{children}</div>
        {footer && <div className="px-4 py-3 border-t border-white/5 shrink-0">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
